import { type SiteContent } from "../types/siteContent";

export class DownloadRateLimitError extends Error {
  retryAfterSeconds?: number;

  constructor(message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "DownloadRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export type User = {
  id: string;
  email: string | null;
  phone: string | null;
  nickname: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  role: "USER" | "DEVELOPER" | string;
  status: string;
  termsAcceptedAt: string | null;
  privacyAcceptedAt: string | null;
  agreementVersion: string | null;
  createdAt: string;
  lastLoginAt: string | null;
};

export type AuthSessionState = {
  rememberMe: boolean;
};

export type QuotaRefreshPeriod = "DAILY" | "THIRTY_DAYS";

export type MembershipPlan = {
  // Public and admin plan responses share this shape. Orders copy price and
  // duration at creation time, so editing a plan later does not rewrite history.
  id: string;
  code: string;
  name: string;
  description: string;
  priceCents: number;
  priceLabel: string;
  currency: string;
  durationDays: number;
  dailyPointLimit: number;
  imageGenerationCostPoints: number;
  allowImageGeneration: boolean;
  // Quota and image pricing are separate knobs: quotaRefreshPeriod controls
  // when membership buckets are granted, while imageGenerationCostPoints
  // controls how many points one image-generation action consumes.
  quotaRefreshPeriod: QuotaRefreshPeriod;
  maxPluginSessions: number;
  features: string[];
  isActive: boolean;
  sortOrder: number;
  archivedAt: string | null;
};

export type PointPackagePlan = {
  id: string;
  code: string;
  name: string;
  description: string;
  priceCents: number;
  priceLabel: string;
  currency: string;
  pointAmount: number;
  validityDays: number;
  isActive: boolean;
  sortOrder: number;
  archivedAt: string | null;
};

export type Membership = {
  status: string;
  startedAt: string;
  expiresAt: string;
  plan: MembershipPlan;
};

export type MembershipPeriod = {
  id: string;
  source: string;
  startedAt: string;
  expiresAt: string;
  durationDays: number;
  note: string | null;
  createdAt: string;
  plan: MembershipPlan;
  userEmail: string | null;
  orderNo: string | null;
  adminEmail: string | null;
};

export type PointBalanceCompat = {
  // Field meaning:
  // - balancePoints: remaining active bucket balance before reservations.
  // - reservedPoints: quota held by in-flight reserve calls.
  // - availablePoints: actually spendable quota after subtracting reservations.
  // - totalUsedPoints/currentMonthUsedPoints: settled point usage counters.
  balancePoints: number;
  reservedPoints: number;
  availablePoints: number;
  totalGrantedPoints: number;
  totalUsedPoints: number;
  currentMonthUsedPoints: number;
};

export type PointBalanceState = PointBalanceCompat & {
  // CANONICAL website/admin balance shape returned by `/api/points/balance`.
  //
  // New UI should prefer the explicit fields below instead of inferring product
  // state from the legacy compatibility keys. This is the frontend side of the
  // PointBucket is the balance authority; product UI reads the bucket-aware
  // fields that state their scope directly.
  pointUsedPoints: number;
  bucketUsedPoints: number;
  membershipDailyLimit: number;
  membershipDailyUsedPoints: number;
  membershipDailyReservedPoints: number;
  membershipDailyAvailablePoints: number;
  packageAvailablePoints: number;
  nextExpiringPackage: {
    id: string;
    source: string;
    availablePoints: number;
    expiresAt: string;
  } | null;
  buckets: Array<{
    id: string;
    source: string;
    totalPoints: number;
    usedPoints: number;
    reservedPoints: number;
    availablePoints: number;
    startsAt: string;
    expiresAt: string;
    note: string | null;
  }>;
  updatedAt: string;
};

export type PointBalance = PointBalanceState;

export type PointEvent = {
  id: string;
  type: string;
  amountPoints: number;
  balanceAfterPoints: number | null;
  reservedAfterPoints: number | null;
  note: string | null;
  createdAt: string;
  bucket: {
    id: string;
    source: string;
    totalPoints: number;
    usedPoints: number;
    reservedPoints: number;
    startsAt: string;
    expiresAt: string;
    note: string | null;
  } | null;
  source: string | null;
  orderNo: string | null;
  adminEmail: string | null;
  requestId: string | null;
  provider: string | null;
  model: string | null;
  metadata: unknown;
};

export type PointUsage = {
  requestId: string;
  provider: string | null;
  model: string | null;
  estimatedPoints: number;
  reservedPoints: number;
  actualPoints: number | null;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  settledAt: string | null;
  releasedAt: string | null;
};

export type ImageGenerationReservePayload = {
  requestId: string;
  imageCount: number;
  provider?: string;
  model?: string;
};

export type ImageGenerationReportPayload = {
  requestId: string;
  status: "SUCCEEDED" | "FAILED";
  imageCount?: number;
  provider?: string;
  model?: string;
  errorMessage?: string;
};

export type ImageGenerationPointResult = {
  usage: PointUsage;
  balance: PointBalanceState;
  chargedPoints: number;
  allowImageGeneration?: boolean;
};

export type AdminPointBalance = Omit<PointBalance, "currentMonthUsedPoints" | "updatedAt"> & {
  updatedAt: string | null;
};

export type AdminManagedUser = User & {
  updatedAt: string;
  pointBalance: AdminPointBalance;
  membership: {
    status: string;
    startedAt: string;
    expiresAt: string;
    plan: {
      id: string;
      code: string;
      name: string;
    } | null;
  } | null;
  downloadCount: number;
  orderCount: number;
};

export type AdminUserDownloadLog = {
  id: string;
  fileVersion: string;
  filename: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type AdminUserDetail = AdminManagedUser & {
  downloadLogs: AdminUserDownloadLog[];
};

export type AdminPointAdjustmentPayload = {
  // Admin grants create POINT_PACKAGE buckets. Admin debits consume existing
  // buckets with the same priority as real plugin usage.
  direction: "grant" | "debit";
  amountPoints: number;
  validityDays?: number;
  note: string;
};

export type AdminUserStatus = "ACTIVE" | "DISABLED" | "DELETED";
export type AdminUserRole = "USER" | "DEVELOPER";
export type AdminUserStatusFilter = AdminUserStatus | "ALL" | "NOT_DELETED";
export type AdminUserRoleFilter = AdminUserRole | "ALL";
export type AdminUserSortBy = "account" | "status" | "createdAt" | "lastLoginAt";
export type SortDirection = "asc" | "desc";

export type PageParams = {
  page?: number;
  pageSize?: number;
};

export type AdminAnalyticsRangeMode = "day" | "month" | "all" | "custom";

export type AdminAnalyticsParams = {
  rangeMode?: AdminAnalyticsRangeMode;
  windowSize?: number;
  startDate?: string;
  endDate?: string;
};

export type PaginatedResponse<T> = {
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
} & T;

export type AdminAnalyticsMemberDetail = {
  id: string;
  userId: string;
  email: string | null;
  phone: string | null;
  nickname: string | null;
  role: AdminUserRole;
  userStatus: AdminUserStatus;
  membershipStatus: string;
  planId: string;
  planCode: string;
  planName: string;
  startedAt: string;
  expiresAt: string;
};

export type AdminAnalytics = {
  range: {
    mode: AdminAnalyticsRangeMode;
    bucket: "day" | "month";
    windowSize: number | null;
    startDate: string;
    endDate: string;
    label: string;
  };
  summary: {
    totalUsers: number;
    newUsersThisMonth: number;
    activePaidMembers: number;
    paidMemberRatio: number;
    ordersThisMonth: number;
    totalRevenueCents: number;
    revenueThisMonthCents: number;
    pointUsedTotal: number;
    pointUsedThisMonth: number;
    totalDownloads: number;
    rhinoInstallCount: number;
  };
  trends: {
    userGrowthMonthly: Array<{ label: string; value: number }>;
    revenueMonthly: Array<{ label: string; amountCents: number }>;
    pointUsageDaily: Array<{ label: string; value: number }>;
    downloadsDaily: Array<{ label: string; value: number }>;
    membershipPlanDistribution: Array<{ planId: string; planName: string; count: number }>;
  };
  details: {
    activePaidMembers: AdminAnalyticsMemberDetail[];
    ordersThisMonth: Array<{
      id: string;
      orderNo: string;
      userId: string;
      userEmail: string | null;
      userPhone: string | null;
      userNickname: string | null;
      productType: "MEMBERSHIP" | "POINT_PACKAGE";
      productName: string | null;
      amountCents: number;
      currency: string;
      status: string;
      paidAt: string | null;
      createdAt: string;
    }>;
  };
  updatedAt: string;
};

export type AdminAnalyticsPlanUsers = {
  plan: { id: string; code: string; name: string };
  users: AdminAnalyticsMemberDetail[];
} & Pick<PaginatedResponse<unknown>, "total" | "page" | "pageSize" | "hasMore">;

export type Order = {
  // Order status is the source of truth for checkout/result pages. Frontend
  // redirects or mock buttons never grant membership by themselves.
  id: string;
  orderNo: string;
  status: string;
  amountCents: number;
  amountLabel: string;
  currency: string;
  paymentProvider: string;
  paymentUrl: string | null;
  expiresAt: string;
  paidAt: string | null;
  closedAt: string | null;
  createdAt: string;
  userEmail: string | null;
  productType: "MEMBERSHIP" | "POINT_PACKAGE";
  // Product display and snapshot are copied at order creation so old orders
  // remain readable after admins rename, reprice, or disable products.
  productName: string;
  productSnapshot: unknown;
  plan: MembershipPlan | null;
  membershipPlan: MembershipPlan | null;
  pointPackagePlan: PointPackagePlan | null;
  transactions: Array<{
    id: string;
    provider: string;
    providerTradeNo: string | null;
    status: string;
    amountCents: number;
    createdAt: string;
  }>;
};

export type PaymentWebhookLog = {
  id: string;
  orderNo: string | null;
  provider: string;
  eventType: string;
  providerEventId: string | null;
  valid: boolean;
  handled: boolean;
  errorMessage: string | null;
  createdAt: string;
};

export type DownloadMode = "development" | "production";
export type PaymentMode = "test" | "live";

// Runtime switches are operational state managed from the admin maintenance tab.
// They are fetched separately from CMS content so changes apply without rebuilds
// or publish steps.
export type AppSettings = {
  downloadMode: DownloadMode;
  paymentMode: PaymentMode;
};

export type RuntimeProviderConfig = {
  enabled: boolean;
  providerId: string;
  displayName: string;
  baseUrl: string;
  modelName: string;
  apiKey: string;
  proxyUrl: string;
};

export type AppRuntimeConfig = {
  version: number;
  updatedAt: string;
  activeProfileId: string;
  profiles: AppRuntimeConfigProfile[];
};

export type AppRuntimeConfigProfile = {
  id: string;
  name: string;
  description: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  lastActivatedAt?: string;
  gpt: RuntimeProviderConfig;
  chat: RuntimeProviderConfig;
  vision: RuntimeProviderConfig;
  image: RuntimeProviderConfig;
};

export type AdminUser = {
  id: string;
  email: string;
  createdAt: string;
};

export type MediaAsset = {
  id: string;
  kind: string;
  originalName: string;
  filename: string;
  path: string;
  url: string;
  mimeType: string;
  sizeBytes: string;
  isActive: boolean;
  uploadedByAdminEmail: string | null;
  createdAt: string;
};

export type DownloadFile = {
  id: string;
  version: string;
  filename: string;
  path: string;
  sizeBytes: string | null;
  isActive: boolean;
  releaseNotes: string | null;
  uploadedByAdminEmail: string | null;
  createdAt: string;
};

export type DownloadLog = {
  id: string;
  userEmail: string | null;
  fileVersion: string;
  filename: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type SiteStats = {
  // Legacy alias retained so older UI code keeps working after the stats API
  // moved from "unique downloaders" to "total download events".
  installCount: number;
  // Total authenticated installer downloads. This intentionally does not
  // de-duplicate by user: if one account downloads twice, the public download
  // volume increases by two.
  downloadCount: number;
  // Total registered public users. Admin users are stored in a separate table
  // and are not included in this customer-facing number.
  userCount: number;
  // Backend API request counter. `/api/stats` and `/api/health` are excluded on
  // the server so homepage polling and health checks do not inflate the number.
  requestCount: number;
  // Server timestamp reserved for future "last updated" copy or debugging.
  updatedAt: string;
};

export type CanvasSnapshot = {
  kind: string;
  viewport?: Record<string, unknown>;
  nodes?: unknown[];
  edges?: unknown[];
  connections?: unknown[];
  drawingItems?: unknown[];
  [key: string]: unknown;
};

export type CanvasDocument = {
  id: string;
  title: string;
  snapshot: CanvasSnapshot;
  cardMetaPatches: unknown;
  nodeCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CanvasAiImagePayload = {
  sourceRef: string;
  prompt: string;
  intent?: "generate" | "edit";
  aspectRatio?: string;
  size?: string;
  imageSize?: string;
  count?: number;
  image?: unknown;
};

export type CanvasPromptOptimizePayload = {
  sourceRef: string;
  prompt: string;
  input?: unknown;
};

export type AuditLog = {
  id: string;
  adminEmail: string | null;
  action: string;
  details: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
};

export type SiteContentVersion = {
  id: string;
  content: SiteContent;
  publishedAt: string;
  publishedByAdminEmail: string | null;
  note: string | null;
};

export type MaintenanceSummary = {
  publishedAt: string | null;
  updatedAt: string;
  mediaCount: number;
  activeMediaCount: number;
  installerCount: number;
  activeInstaller: DownloadFile | null;
  downloadCount: number;
  appSettings: AppSettings;
  recentAuditLogs: Array<{
    id: string;
    adminEmail: string | null;
    action: string;
    createdAt: string;
  }>;
  backupTargets: string[];
};

type AuthPayload = {
  email?: string;
  phone?: string;
  account?: string;
  nickname?: string;
  password: string;
  verificationCode?: string;
};

type AgreementPayload = {
  termsAccepted: true;
  privacyAccepted: true;
  agreementVersion: "v1";
};

type RegisterPayload = AuthPayload & AgreementPayload;
type WebLoginPayload = AuthPayload & AgreementPayload & {
  rememberMe?: boolean;
  canvasSession?: boolean;
};

type SendCodeResponse = {
  ok: true;
  delivery: "email";
  expiresAt: string;
};

type LoginPayload = AgreementPayload & {
  account: string;
  password: string;
  rememberMe?: boolean;
  canvasSession?: boolean;
  clientType?: "WEB" | "PLUGIN";
  deviceName?: string;
};

type AuthResponse = {
  token?: string;
  expiresAt?: string;
  user: User;
  rememberMe: boolean;
};

type SendCodePayload = {
  account: string;
  accountType: "EMAIL" | "PHONE";
  purpose: "REGISTER" | "LOGIN" | "RESET_PASSWORD";
};

type ResetPasswordPayload = {
  email: string;
  verificationCode: string;
  password: string;
};

type UpdateProfilePayload = {
  nickname: string | null;
  phone: string | null;
};

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  // JSON API helper for both public/user and admin endpoints. Credentials are
  // always included because normal sessions and admin sessions are both stored
  // in httpOnly cookies; the backend decides which cookie is valid for a route.
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed." }));
    throw new Error(error.message || "Request failed.");
  }

  return response.json() as Promise<T>;
}

function withPageParams(url: string, params?: PageParams & Record<string, string | number | boolean | undefined>) {
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

async function upload<T>(url: string, formData: FormData): Promise<T> {
  // Multipart uploads must not set `Content-Type` manually; the browser needs to
  // add the boundary. Credentials stay included so admin upload routes can read
  // the independent admin session cookie.
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed." }));
    throw new Error(error.message || "Request failed.");
  }

  return response.json() as Promise<T>;
}

export const authApi = {
  me: () => request<AuthResponse>("/api/auth/me"),
  canvasSession: () => request<{ ok: true; user: User }>("/api/auth/canvas-session"),
  login: (payload: LoginPayload) =>
    request<AuthResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ clientType: "WEB", ...payload })
    }),
  register: (payload: RegisterPayload) =>
    request<AuthResponse>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  logout: () =>
    request<{ ok: true }>("/api/auth/logout", {
      method: "POST",
      body: JSON.stringify({})
    }),
  refresh: () =>
    request<AuthResponse>("/api/auth/refresh", {
      method: "POST",
      body: JSON.stringify({})
    }),
  sendCode: (payload: SendCodePayload) =>
    request<SendCodeResponse>("/api/auth/send-code", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  resetPassword: (payload: ResetPasswordPayload) =>
    request<{ ok: true }>("/api/auth/password-reset", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateProfile: (payload: UpdateProfilePayload) =>
    request<{ user: User }>("/api/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(payload)
    })
};

export const api = {
  // Public homepage content. The server returns only the published snapshot, so
  // visitors never see in-progress admin drafts.
  siteContent: () => request<{ content: SiteContent; publishedAt: string | null }>("/api/site-content"),
  appSettings: () => request<{ settings: AppSettings }>("/api/app-settings"),
  // Informational hero metrics. App ignores failures and renders placeholders.
  stats: () => request<{ stats: SiteStats }>("/api/stats"),
  membershipPlans: () => request<{ plans: MembershipPlan[] }>("/api/membership/plans"),
  pointPackagePlans: () => request<{ plans: PointPackagePlan[] }>("/api/point-packages"),
  membership: () => request<{ membership: Membership | null }>("/api/membership/me"),
  membershipPeriods: () => request<{ periods: MembershipPeriod[] }>("/api/membership/periods"),
  updateProfile: authApi.updateProfile,
  pointBalance: () => request<{ balance: PointBalanceState }>("/api/points/balance"),
  // App image-generation accounting is a two-step lifecycle. reserve enforces
  // membership entitlement and reserves server-priced points before model work;
  // report only settles or releases that existing reservation.
  reserveImageGeneration: (payload: ImageGenerationReservePayload) =>
    request<ImageGenerationPointResult>("/api/points/image-generation/reserve", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  reportImageGeneration: (payload: ImageGenerationReportPayload) =>
    request<ImageGenerationPointResult>("/api/points/image-generation/report", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  pointEvents: () => request<{ events: PointEvent[] }>("/api/points/events"),
  canvases: () => request<{ canvases: CanvasDocument[] }>("/api/canvases"),
  createCanvas: (title?: string) =>
    request<{ canvas: CanvasDocument }>("/api/canvases", {
      method: "POST",
      body: JSON.stringify({ title })
    }),
  canvas: (id: string) => request<{ canvas: CanvasDocument }>(`/api/canvases/${encodeURIComponent(id)}`),
  saveCanvasSnapshot: (id: string, payload: { title?: string; snapshot: CanvasSnapshot; cardMetaPatches?: unknown }) =>
    request<{ canvas: CanvasDocument }>(`/api/canvases/${encodeURIComponent(id)}/snapshot`, {
      method: "PUT",
      body: JSON.stringify(payload)
    }),
  deleteCanvas: (id: string) =>
    request<{ ok: true }>(`/api/canvases/${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  runCanvasAiImage: (id: string, payload: CanvasAiImagePayload) =>
    request<{
      sourceRef: string;
      success: boolean;
      provider?: string;
      model?: string;
      prompt?: string;
      size?: string;
      aspectRatio?: string;
      imageSize?: string;
      generatedAtUtc?: string;
      images?: Array<Record<string, unknown>>;
      pointUsage?: number;
      pointUsageLabel?: string;
      error?: string;
    }>(`/api/canvases/${encodeURIComponent(id)}/ai-image`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  optimizeCanvasPrompt: (id: string, payload: CanvasPromptOptimizePayload) =>
    request<{ sourceRef: string; success: boolean; prompt?: string; pointUsage?: number; pointUsageLabel?: string; error?: string }>(
      `/api/canvases/${encodeURIComponent(id)}/prompt-optimize`,
      {
        method: "POST",
        body: JSON.stringify(payload)
      }
    ),
  orders: () => request<{ orders: Order[] }>("/api/orders"),
  order: (orderNo: string) => request<{ order: Order }>(`/api/orders/${encodeURIComponent(orderNo)}`),
  // The browser submits only plan id. Amount and duration are loaded on the
  // server from MembershipPlan so users cannot manipulate price in DevTools.
  createOrder: (productId: string, productType: "MEMBERSHIP" | "POINT_PACKAGE" = "MEMBERSHIP") =>
    request<{ order: Order }>("/api/orders", {
      method: "POST",
      body: JSON.stringify({ productType, productId })
    }),
  me: async () => {
    try {
      return await authApi.me();
    } catch (error) {
      if (error instanceof Error && error.message === "Login required.") {
        return { user: null, rememberMe: false };
      }
      throw error;
    }
  },
  canvasSession: () => authApi.canvasSession(),
  login: (payload: WebLoginPayload) =>
    authApi.login({
      account: payload.account || payload.email || payload.phone || "",
      password: payload.password,
      rememberMe: "rememberMe" in payload ? payload.rememberMe : undefined,
      canvasSession: payload.canvasSession,
      termsAccepted: payload.termsAccepted,
      privacyAccepted: payload.privacyAccepted,
      agreementVersion: payload.agreementVersion
    }).then(({ user, rememberMe }) => ({ user, rememberMe })),
  register: (payload: RegisterPayload) => authApi.register(payload),
  logout: () => authApi.logout(),
  refresh: () => authApi.refresh(),
  sendCode: (payload: SendCodePayload) => authApi.sendCode(payload),
  resetPassword: (payload: ResetPasswordPayload) => authApi.resetPassword(payload),
  downloadLatest: async () => {
    // Keep the JSON preflight separate from the binary download. Fetching the
    // installer itself would require `response.blob()`, which waits for the full
    // file before resolving and makes the button feel unresponsive on large
    // installers. After this preflight succeeds, the browser handles the real
    // download natively and can show progress immediately.
    const response = await fetch("/api/downloads/latest/check", {
      credentials: "include",
      cache: "no-store"
    });

    if (response.status === 401) {
      throw new Error("Please sign in before downloading.");
    }

    if (response.status === 429) {
      const error = await response.json().catch(() => ({ message: "Too many download attempts, please try again later." }));
      throw new DownloadRateLimitError(error.message || "Too many download attempts, please try again later.", error.retryAfterSeconds);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Download failed." }));
      throw new Error(error.message || "Download failed.");
    }

    const anchor = document.createElement("a");
    anchor.href = `/api/downloads/latest?t=${Date.now()}`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  },
  recordRhinoInstall: async () => {
    const response = await fetch("/api/downloads/rhino-install", {
      method: "POST",
      credentials: "include",
      keepalive: true
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Install count failed." }));
      throw new Error(error.message || "Install count failed.");
    }
  }
};

export const adminApi = {
  // Admin endpoints are kept in a separate object to make the privilege boundary
  // visible to frontend maintainers. They use the admin cookie and are rejected
  // by the backend for normal download users.
  me: () => request<{ admin: AdminUser | null }>("/api/admin/me"),
  login: (payload: AuthPayload) =>
    request<{ admin: AdminUser }>("/api/admin/login", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  logout: () =>
    request<{ ok: true }>("/api/admin/logout", {
      method: "POST",
      body: JSON.stringify({})
    }),
  content: () =>
    request<{ draft: SiteContent; published: SiteContent | null; publishedAt: string | null; updatedAt: string }>(
      "/api/admin/content"
    ),
  saveContent: (content: SiteContent) =>
    request<{ draft: SiteContent; updatedAt: string }>("/api/admin/content", {
      method: "PUT",
      body: JSON.stringify({ content })
    }),
  publishContent: () =>
    request<{ published: SiteContent; publishedAt: string | null }>("/api/admin/content/publish", {
      method: "POST",
      body: JSON.stringify({})
    }),
  publishContentWithNote: (note: string) =>
    request<{ published: SiteContent; publishedAt: string | null }>("/api/admin/content/publish", {
      method: "POST",
      body: JSON.stringify({ note })
    }),
  contentVersions: () => request<{ versions: SiteContentVersion[] }>("/api/admin/content/versions"),
  restoreContentVersion: (id: string) =>
    request<{ draft: SiteContent; restoredFromVersionId: string; updatedAt: string }>(
      `/api/admin/content/versions/${encodeURIComponent(id)}/restore-draft`,
      {
        method: "POST",
        body: JSON.stringify({})
      }
    ),
  uploadMedia: (kind: "image" | "video", file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return upload<{ asset: MediaAsset }>(`/api/admin/media/${kind}`, formData);
  },
  media: (params?: PageParams & { kind?: "image" | "video"; active?: "true" | "false" }) =>
    request<PaginatedResponse<{ assets: MediaAsset[] }>>(withPageParams("/api/admin/media", params)),
  updateMedia: (id: string, patch: { isActive?: boolean; originalName?: string }) =>
    request<{ asset: MediaAsset }>(`/api/admin/media/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
  replaceMedia: (id: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return upload<{ asset: MediaAsset }>(`/api/admin/media/${encodeURIComponent(id)}/replace`, formData);
  },
  deleteMedia: (id: string) =>
    request<{ ok: true }>(`/api/admin/media/${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  uploadInstaller: (version: string, releaseNotes: string, file: File) => {
    // Installer upload creates a release-history record but does not activate it.
    // Version, notes, and file travel together so the backend can audit a single
    // operator action before a later explicit activation step.
    const formData = new FormData();
    formData.append("version", version);
    formData.append("releaseNotes", releaseNotes);
    formData.append("file", file);
    return upload<{ file: DownloadFile }>("/api/admin/installers", formData);
  },
  activateInstaller: (id: string) =>
    request<{ file: DownloadFile }>(`/api/admin/installers/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  installers: () => request<{ files: DownloadFile[] }>("/api/admin/installers"),
  downloadLogs: () => request<{ logs: DownloadLog[] }>("/api/admin/download-logs"),
  auditLogs: () => request<{ logs: AuditLog[] }>("/api/admin/audit-logs"),
  maintenance: () => request<{ maintenance: MaintenanceSummary }>("/api/admin/maintenance"),
  appSettings: () => request<{ settings: AppSettings }>("/api/admin/app-settings"),
  updateAppSettings: (patch: Partial<AppSettings>) =>
    request<{ settings: AppSettings }>("/api/admin/app-settings", {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
  // Runtime credentials/configuration are operational state. Never allow a
  // browser or intermediary cache to replay an older profile after an admin
  // saves a new URL, model, or key.
  appRuntimeConfig: () => request<{ config: AppRuntimeConfig }>(`/api/admin/app-runtime-config?ts=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" }
  }),
  updateAppRuntimeConfig: (patch: Partial<Pick<AppRuntimeConfigProfile, "gpt" | "chat" | "vision" | "image">>) =>
    request<{ config: AppRuntimeConfig }>("/api/admin/app-runtime-config", {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
  createAppRuntimeConfigProfile: (payload: { name: string; description?: string } & Partial<Pick<AppRuntimeConfigProfile, "gpt" | "chat" | "vision" | "image">>) =>
    request<{ config: AppRuntimeConfig; profile?: AppRuntimeConfigProfile }>("/api/admin/app-runtime-config/profiles", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateAppRuntimeConfigProfile: (id: string, payload: { name?: string; description?: string } & Partial<Pick<AppRuntimeConfigProfile, "gpt" | "chat" | "vision" | "image">>) =>
    request<{ config: AppRuntimeConfig }>(`/api/admin/app-runtime-config/profiles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  duplicateAppRuntimeConfigProfile: (id: string) =>
    request<{ config: AppRuntimeConfig }>(`/api/admin/app-runtime-config/profiles/${encodeURIComponent(id)}/duplicate`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  activateAppRuntimeConfigProfile: (id: string) =>
    request<{ config: AppRuntimeConfig }>(`/api/admin/app-runtime-config/profiles/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  archiveAppRuntimeConfigProfile: (id: string) =>
    request<{ config: AppRuntimeConfig }>(`/api/admin/app-runtime-config/profiles/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  membershipPlans: (params?: { includeArchived?: boolean }) => {
    const suffix = params?.includeArchived ? "?includeArchived=true" : "";
    return request<{ plans: MembershipPlan[] }>(`/api/admin/billing/plans${suffix}`);
  },
  createMembershipPlan: (payload: {
    code: string;
    name: string;
    description: string;
    priceCents: number;
    durationDays: number;
    dailyPointLimit: number;
    imageGenerationCostPoints: number;
    allowImageGeneration: boolean;
    quotaRefreshPeriod: QuotaRefreshPeriod;
    maxPluginSessions: number;
    features: string[];
    isActive: boolean;
    sortOrder: number;
  }) =>
    request<{ plan: MembershipPlan }>("/api/admin/billing/plans", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateMembershipPlan: (id: string, payload: {
    name: string;
    description: string;
    priceCents: number;
    durationDays: number;
    dailyPointLimit: number;
    imageGenerationCostPoints: number;
    allowImageGeneration: boolean;
    quotaRefreshPeriod: QuotaRefreshPeriod;
    maxPluginSessions: number;
    features: string[];
    isActive: boolean;
    sortOrder: number;
  }) =>
    request<{ plan: MembershipPlan }>(`/api/admin/billing/plans/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    }),
  archiveMembershipPlan: (id: string) =>
    request<{ plan: MembershipPlan }>(`/api/admin/billing/plans/${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  pointPackagePlans: () => request<{ plans: PointPackagePlan[] }>("/api/admin/billing/point-packages"),
  createPointPackagePlan: (payload: {
    code: string;
    name: string;
    description: string;
    priceCents: number;
    pointAmount: number;
    validityDays: number;
    isActive: boolean;
    sortOrder: number;
  }) =>
    request<{ plan: PointPackagePlan }>("/api/admin/billing/point-packages", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updatePointPackagePlan: (id: string, payload: {
    name: string;
    description: string;
    priceCents: number;
    pointAmount: number;
    validityDays: number;
    isActive: boolean;
    sortOrder: number;
  }) =>
    request<{ plan: PointPackagePlan }>(`/api/admin/billing/point-packages/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    }),
  archivePointPackagePlan: (id: string) =>
    request<{ plan: PointPackagePlan }>(`/api/admin/billing/point-packages/${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  orders: (params?: PageParams) => request<PaginatedResponse<{ orders: Order[] }>>(withPageParams("/api/admin/billing/orders", params)),
  paymentWebhookLogs: (params?: PageParams) => request<PaginatedResponse<{ logs: PaymentWebhookLog[] }>>(withPageParams("/api/admin/billing/webhook-logs", params)),
  membershipPeriods: (params?: PageParams) => request<PaginatedResponse<{ periods: MembershipPeriod[] }>>(withPageParams("/api/admin/billing/membership-periods", params)),
  analytics: (params?: AdminAnalyticsParams) => {
    const search = new URLSearchParams();
    if (params?.rangeMode) search.set("rangeMode", params.rangeMode);
    if (params?.windowSize) search.set("windowSize", String(params.windowSize));
    if (params?.startDate) search.set("startDate", params.startDate);
    if (params?.endDate) search.set("endDate", params.endDate);
    const suffix = search.toString();
    return request<{ analytics: AdminAnalytics }>(`/api/admin/analytics${suffix ? `?${suffix}` : ""}`);
  },
  analyticsActivePaidMembers: (params?: PageParams) => request<PaginatedResponse<{ users: AdminAnalyticsMemberDetail[] }>>(withPageParams("/api/admin/analytics/active-paid-members", params)),
  analyticsOrdersThisMonth: (params?: PageParams) => request<PaginatedResponse<{ orders: AdminAnalytics["details"]["ordersThisMonth"] }>>(withPageParams("/api/admin/analytics/orders-this-month", params)),
  analyticsPlanUsers: (planId: string, params?: PageParams) => request<AdminAnalyticsPlanUsers>(withPageParams(`/api/admin/analytics/membership-plans/${planId}/users`, params)),
  pointBuckets: (params?: PageParams) => request<PaginatedResponse<{ buckets: Array<{
    id: string;
    userEmail: string | null;
    source: string;
    planName: string | null;
    totalPoints: number;
    usedPoints: number;
    reservedPoints: number;
    availablePoints: number;
    startsAt: string;
    expiresAt: string;
    orderNo: string | null;
    note: string | null;
  }> }>>(withPageParams("/api/admin/billing/point-buckets", params)),
  users: (params?: {
    search?: string;
    page?: number;
    pageSize?: number;
    status?: AdminUserStatusFilter;
    role?: AdminUserRoleFilter;
    planId?: string;
    sortBy?: AdminUserSortBy;
    sortDirection?: SortDirection;
  }) => {
    const search = new URLSearchParams();
    if (params?.search) search.set("search", params.search);
    if (params?.page) search.set("page", String(params.page));
    if (params?.pageSize) search.set("pageSize", String(params.pageSize));
    if (params?.status) search.set("status", params.status);
    if (params?.role) search.set("role", params.role);
    if (params?.planId) search.set("planId", params.planId);
    if (params?.sortBy) search.set("sortBy", params.sortBy);
    if (params?.sortDirection) search.set("sortDirection", params.sortDirection);
    const query = search.toString();
    return request<{ users: AdminManagedUser[]; total: number; page: number; pageSize: number }>(
      `/api/admin/users${query ? `?${query}` : ""}`
    );
  },
  user: (id: string) => request<{ user: AdminUserDetail }>(`/api/admin/users/${encodeURIComponent(id)}`),
  updateUserStatus: (id: string, status: AdminUserStatus) =>
    request<{ user: AdminManagedUser }>(`/api/admin/users/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    }),
  updateUserRole: (id: string, role: AdminUserRole) =>
    request<{ user: AdminManagedUser }>(`/api/admin/users/${encodeURIComponent(id)}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role })
    }),
  adjustUserMembership: (id: string, payload: { planId: string; durationDays: number; note?: string }) =>
    request<{ membership: Membership }>(`/api/admin/users/${encodeURIComponent(id)}/membership/adjust`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  adjustUserPoints: (id: string, payload: AdminPointAdjustmentPayload) =>
    request<{ balance: AdminPointBalance }>(`/api/admin/users/${encodeURIComponent(id)}/points/adjust`, {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  userPointEvents: (id: string) => request<{ events: PointEvent[] }>(`/api/admin/users/${encodeURIComponent(id)}/point-events`)
};

